import { MENU, CATEGORIES, type MenuItem } from "@/data/menu";
import type { MenuItemRow } from "@/lib/menu.functions";

export type UnifiedMenuItem = MenuItem;

/**
 * DB is the source of truth. When DB has any rows, only published DB rows are shown —
 * deleting from admin removes the item everywhere. Seed MENU is a fallback used only
 * when the DB is empty (fresh project / offline preview) and provides image fallbacks
 * by slug for DB rows that lack an image_url.
 */
export function mergeMenu(dbRows: MenuItemRow[] | undefined | null): UnifiedMenuItem[] {
  const seedBySlug = new Map<string, MenuItem>();
  const seedIndex = new Map<string, number>();
  MENU.forEach((s, idx) => {
    seedBySlug.set(s.slug, s);
    seedIndex.set(s.slug, idx);
  });

  // Fallback: no DB rows at all → show seed so the site is never blank.
  if (!dbRows || dbRows.length === 0) {
    return [...MENU].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  }

  const items: UnifiedMenuItem[] = [];
  for (const r of dbRows) {
    if (!r.published) continue;
    const seed = seedBySlug.get(r.slug);
    items.push({
      slug: r.slug,
      name: r.name,
      category: r.category as MenuItem["category"],
      price: r.price,
      tagline: r.tagline,
      description: r.description,
      ingredients: r.ingredients?.length ? r.ingredients : seed?.ingredients ?? [],
      pairings: r.pairings?.length ? r.pairings : seed?.pairings,
      image: r.image_url || seed?.image || "",
      badge: r.badge ?? seed?.badge,
      sort_order: r.sort_order ?? seed?.sort_order ?? 0,
    });
  }

  return items.sort((a, b) => {
    const sa = a.sort_order ?? 0;
    const sb = b.sort_order ?? 0;
    if (sa !== sb) return sa - sb;
    const ia = seedIndex.get(a.slug) ?? 9999;
    const ib = seedIndex.get(b.slug) ?? 9999;
    return ia - ib;
  });
}


export function mergeMenuCategories(items: UnifiedMenuItem[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of CATEGORIES) {
    if (items.some((i) => i.category === c)) {
      seen.add(c);
      out.push(c);
    }
  }
  for (const i of items) {
    if (!seen.has(i.category)) {
      seen.add(i.category);
      out.push(i.category);
    }
  }
  return out;
}

export function findMergedItem(
  slug: string,
  dbRows: MenuItemRow[] | undefined | null,
): UnifiedMenuItem | undefined {
  return mergeMenu(dbRows).find((m) => m.slug === slug);
}
